<template>
  <Dialog
    @submit="onSubmit"
    :title="title"
  >
    <form @submit.prevent="onSubmit">
      <div class="form-group">
        <label for="name">Name*</label>
        <input
          type="text"
          v-validate="'required'"
          id="name"
          name="name"
          class="form-control"
          :class="{'is-invalid': errors.has('name')}"
          v-model.trim="form.name"
          autofocus
        >
        <small v-show="errors.has('name')" class="invalid-feedback">
          {{ errors.first('name') }}
        </small>
      </div>

      <div class="form-group">
        <label for="email">Email*</label>
        <input type="email" v-validate="'required|emailStrict'" id="email" name="email" class="form-control" :class="{'is-invalid': errors.has('email')}" v-model.trim="form.email">
        <small v-show="errors.has('email')" class="invalid-feedback">
          {{ errors.first('email') }}
        </small>
      </div>

      <div class="form-group">
        <label for="phone">Phone</label>
        <input
          type="text"
          v-mask="'(###) ###-####'"
          v-model="form.phone"
          v-validate="{ regex: /^\(\d{3}\) \d{3}-\d{4}$/ }"
          id="phone" name="phone" class="form-control"
          :class="{'is-invalid': errors.has('phone')}"
          placeholder="(999) 999-9999"
        />
        <small v-show="errors.has('phone')" class="invalid-feedback">
          {{ errors.first('phone') }}
        </small>
      </div>

      <div class="form-group">
        <label for="role">Role*</label>
        <multiselect
          id="role"
          :options="roles"
          fieldName="role_type"
          v-model="form.role"
          :defaultValue="form.role"
          v-validate="'required'"
          name="role"
          data-vv-as="Role"
          :isValid="!errors.has('role')"
          class="form-control"
        ></multiselect>
        <span
          v-show="errors.has('role')"
          class="error-message"
        >
          {{ errors.first('role') }}
        </span>
      </div>

      <div class="form-group" v-if="showCompanyInput">
        <label for="company">Company*</label>
        <multiselect
          id="company"
          :options="prepareCompanies"
          fieldName="companyName"
          v-model="form.company"
          v-validate="'required'"
          name="company"
          data-vv-as="Company"
          :isValid="!errors.has('company')"
          class="form-control"
        ></multiselect>
        <span
          v-show="errors.has('company')"
          class="error-message"
        >
          {{ errors.first('company') }}
        </span>
      </div>

      <div class="form-group">
        <label for="password">Password*</label>
        <input
          type="password"
          id="password"
          v-validate="passwordValidation"
          name="password" class="form-control"
          :class="{'is-invalid': errors.has('password')}"
          v-model.trim="form.password"
          ref="password"
        >
        <small v-show="errors.has('password')" class="invalid-feedback">
          {{ errors.first('password') }}
        </small>
      </div>

      <div class="form-group">
        <label for="password2">Confirm Password*</label>
        <input
          type="password"
          id="password2"
          v-validate="form.password ? 'required|confirmed:password' : ''"
          data-vv-as="confirm password"
          name="password2"
          class="form-control"
          :class="{'is-invalid': errors.has('password2')}"
          v-model.trim="form.password2"
        >
        <small v-show="errors.has('password2')" class="invalid-feedback">
          {{ errors.first('password2') }}
        </small>
      </div>
      <div class="form-group">
        <label for="password2">Status</label>
        <switches v-model="form.status" color="green"></switches>
      </div>
    </form>
  </Dialog>
</template>

<script>
import Switches from 'vue-switches';
import Dialog from '@/components/Dialog';
import ModalMixin from '@/mixins/ModalMixin';

import USER from '@/graphql/Users/User.gql';
import USER_ROLE from '@/graphql/Users/UserRole.gql';
import ADD_USER from '@/graphql/Users/AddUser.gql';
import UPDATE_USER from '@/graphql/Users/UpdateUser.gql';
import COMPANIES from '@/graphql/Companies/Companies.gql';

import Multiselect from '@/components/CustomMultiselect';

export default {
  name: 'add-user',
  mixins: [ModalMixin],
  components: {
    Dialog,
    Switches,
    Multiselect,
  },
  props: {
    userId: Number,
  },

  data() {
    return {
      form: {
        role: null,
        company: null,
        status: true,
      },
      roles: [],
      showSpinner: false,
      companies: [],
    };
  },

  computed: {
    title() {
      return this.userId ? 'Edit Account' : 'Create New Account';
    },
    prepareCompanies() {
      return this.companies.map(company => ({
        id: company.id,
        companyName: company.companyName,
      }));
    },
    showCompanyInput() {
      if (!this.form.role || !this.form.role.id || !this.roles.length) {
        return false;
      }

      return this.roles.find(role => +role.id === +this.form.role.id).role_type === 'client';
    },
    passwordValidation() {
      return this.userId ? 'min:6' : 'required|min:6';
    },
  },

  methods: {
    async onSubmit() {
      const isValid = await this.$validator.validateAll();

      if (isValid) {
        // form data
        const user = {
          name: this.form.name,
          email: this.form.email,
          phone: this.form.phone,
          roleId: +this.form.role.id,
          password: this.form.password,
          status: !!this.form.status,
        };

        if (this.form.company) {
          user.companyId = parseInt(this.form.company.id);
        }

        let mutation;
        // update existing user
        if (this.userId) {
          mutation = this.updateUser(this.userId, user);
        } else {
          // create a new user
          mutation = this.addUser(user);
        }

        this.showSpinner = true;

        mutation
          .then(() => {
            // hide modal
            this.close();
          })
          .catch(e => {
            // highlight invalid fields
            if (e.message.includes('ER_DUP_ENTRY')) {
              this.$validator.errors.add('email', 'This email was registered');
            } else if (e.graphQLErrors[0].data.field) {
              this.$validator.errors.add(e.graphQLErrors[0].data.field, e.graphQLErrors[0].message);
            } else {
              this.$notify({
                group: 'error',
                type: 'error',
                text: e.message,
              });
            }
          })
          .finally(() => {
            this.showSpinner = false;
          });
      }
    },

    addUser(user) {
      return this.$apollo.mutate({
        // Query
        mutation: ADD_USER,
        variables: {
          input: user,
        },
        refetchQueries: ({ data }) => {
          return data.addUser.success ? ['users'] : [];
        },
      });
    },

    updateUser(id, user) {
      return this.$apollo.mutate({
        // Query
        mutation: UPDATE_USER,
        variables: {
          id,
          input: {
            ...user,
            companyId: this.showCompanyInput ? parseInt(user.companyId) : null,
          },
        },
        refetchQueries: ({ data }) => {
          return data.updateUser.success ? ['users'] : [];
        },
      });
    },
  },

  apollo: {
    user: {
      query: USER,
      fetchPolicy: 'network-only',
      result({ data, loading }) {
        if (loading) {
          return;
        }
        // fulfill form
        this.form = {
          ...data.user,
          role: { id: data.user.role.id, role_type: data.user.role.role_type },
          company: {
            id: data.user.company ? data.user.company.id : null,
            companyName: data.user.company ? data.user.company.companyName : null,
          },
        };
      },
      variables() {
        return {
          id: this.userId,
        };
      },
      skip() {
        return !this.userId;
      },
    },

    roles: {
      query: USER_ROLE,
      fetchPolicy: 'network-only',
      result({ data, loading }) {
        if (!loading) {
          this.roles = data.roles.map(({ id, role_type }) => ({
            id,
            role_type,
          }));
        }
      },
    },

    companies: {
      query: COMPANIES,
      fetchPolicy: 'network-only',
      result({ data, loading }) {
        if (!loading) {
          this.companies = data.companies.result.map(comp => ({
            id: comp.id,
            companyName: comp.companyName,
          }));
        }
      },
      variables() {
        return {
          limit: 0,
        };
      },
      skip() {
        return !this.showCompanyInput;
      },
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.form-group {
  margin-bottom: 12px;
}
.vue-switcher {
  margin-left: 20px;
  margin-bottom: 0;
  top: 2px;
}
</style>



// WEBPACK FOOTER //
// src/modals/AddUser.vue